import { PageHero } from "@/components/PageHero";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { Users, Printer, Plus, Trash2, Share2, Mail, MessageSquare, Copy, X, Sparkles, Wand2, Clock } from "lucide-react";
import { toast } from "sonner";
import { shareViaText, textShareToast } from "@/lib/sms";

const SEED_TASKS = [
    "Bring dinner one evening this week",
    "Take the loved one for a 1-hour walk so I can rest",
    "Pick up prescriptions from the pharmacy",
    "Sit with them on Saturday morning (3 hours)",
    "Help with laundry or housework",
    "Drive to a medical appointment",
];

const APP_URL = (typeof window !== "undefined" ? window.location.origin : "");

export default function Family() {
    const sid = getSessionId();
    const [tasks, setTasks] = useState(SEED_TASKS.map((t, i) => ({ id: `s${i}`, text: t, done: false })));
    const [newTask, setNewTask] = useState("");
    const [profile, setProfile] = useState({ caregiver_name: "", loved_one_name: "" });
    const [shareOpen, setShareOpen] = useState(false);
    const [shareTo, setShareTo] = useState({ name: "", email: "" });

    // Phase D — personalized family support
    const [suggestions, setSuggestions] = useState(null); // {stage, bucket, tasks, ...} | null
    const SITUATION_KEY = `mlcn_situation_draft_${sid}`;
    const [situationText, setSituationText] = useState(() => {
        try { return localStorage.getItem(SITUATION_KEY) || ""; } catch { return ""; }
    });
    const [situationLoading, setSituationLoading] = useState(false);
    const [hasDrafted, setHasDrafted] = useState(false);

    // Autosave to localStorage on every change (small payload, no debounce needed).
    useEffect(() => {
        try {
            if (situationText) localStorage.setItem(SITUATION_KEY, situationText);
            else localStorage.removeItem(SITUATION_KEY);
        } catch { /* storage full or blocked — silent */ }
    }, [situationText, SITUATION_KEY]);

    useEffect(() => {
        api.get(`/profile/${sid}`).then(r => {
            setProfile({
                caregiver_name: r.data.caregiver_name || "",
                loved_one_name: r.data.loved_one_name || "",
            });
        }).catch(err => {
            if (process.env.NODE_ENV !== "production") {
                console.warn("Family: profile load failed", err);
            }
        });
        // Load stage-based suggestions
        api.get(`/family/suggested-tasks/${sid}`).then(r => {
            setSuggestions(r.data && !r.data.no_intake ? r.data : null);
        }).catch(() => { /* ignore — page works without */ });
    }, [sid]);

    const addSuggestion = (text) => {
        let added = false;
        setTasks(prev => {
            if (prev.some(t => t.text === text)) return prev;
            added = true;
            return [...prev, { id: `sg${Date.now()}`, text, done: false }];
        });
        if (added) toast.success("Added to your list.");
        else toast.info("Already on your list.");
    };

    const draftSituation = async () => {
        setSituationLoading(true);
        try {
            // Pass the live task list (with done/not-done) AND any custom note so
            // Lane turns open tasks into concrete asks, briefly acknowledges what
            // the caregiver has already handled, and keeps their own words intact.
            const payloadTasks = tasks.map(t => ({ text: t.text, done: !!t.done }));
            // Suggestions the caregiver explicitly added (i.e. are present in the
            // task list) get an extra nudge to be included as concrete asks.
            const selectedSuggestions = (suggestions?.tasks || [])
                .filter(s => tasks.some(t => t.text === s))
                .map(s => s);
            const r = await api.post(`/family/share-situation/${sid}`, {
                custom_context: situationText,
                tasks: payloadTasks,
                selected_suggestions: selectedSuggestions,
            });
            setSituationText(r.data.summary || "");
            setHasDrafted(true);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't generate. Try again in a moment.";
            toast.error(msg);
        } finally {
            setSituationLoading(false);
        }
    };

    const copySituation = () => {
        const txt = situationText.trim();
        if (!txt) {
            toast.error("Write something — or tap Draft this for me — first.");
            return;
        }
        navigator.clipboard.writeText(txt).then(
            () => {
                toast.success("Copied — your draft is saved for next time.");
            },
            () => toast.error("Copy failed.")
        );
    };

    const emailSituation = () => {
        const txt = situationText.trim();
        if (!txt) {
            toast.error("Write something — or tap Draft this for me — first.");
            return;
        }
        const subject = "An update on our situation";
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(txt)}`;
    };

    const clearSituation = () => {
        setSituationText("");
        setHasDrafted(false);
        try { localStorage.removeItem(SITUATION_KEY); } catch { /* ignore */ }
    };

    const add = () => {
        if (!newTask.trim()) return;
        setTasks([...tasks, { id: `t${Date.now()}`, text: newTask.trim(), done: false }]);
        setNewTask("");
    };
    const toggle = (id) => setTasks(tasks.map(t => t.id === id ? { ...t, done: !t.done } : t));
    const remove = (id) => setTasks(tasks.filter(t => t.id !== id));

    const buildMessage = (toName) => {
        const me = profile.caregiver_name || "me";
        const lo = profile.loved_one_name || "my loved one";
        const greeting = toName ? `Hi ${toName},` : "Hi,";
        const items = tasks.filter(t => !t.done).map(t => `• ${t.text}`).join("\n");
        return `${greeting}

I'm caring for ${lo} and I could really use a hand. Here are specific ways you could help — even one would mean a lot:

${items}

If you can do one, just text me back which one. No need to do everything.

Thank you for showing up.

— ${me}

(I'm using Memory Lane Care Navigator to organize things: ${APP_URL})`;
    };

    const logReferral = (channel, taskCount) =>
        api.post("/family-referral", {
            session_id: sid,
            to_name: shareTo.name,
            to_email: shareTo.email,
            channel,
            task_count: taskCount,
        }).catch(err => {
            if (process.env.NODE_ENV !== "production") {
                console.warn("Family: referral log failed", err);
            }
        });

    const taskCount = tasks.filter(t => !t.done).length;

    const sendEmail = () => {
        const subject = `${profile.loved_one_name ? `Caring for ${profile.loved_one_name}` : "Caring for my loved one"} — a few ways you can help`;
        const body = buildMessage(shareTo.name);
        const to = shareTo.email ? encodeURIComponent(shareTo.email) : "";
        window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        logReferral("email", taskCount);
        toast.success("Opening your email…");
    };

    const sendSms = async () => {
        const body = buildMessage(shareTo.name);
        const method = await shareViaText(body);
        logReferral("sms", taskCount);
        textShareToast(method, toast);
    };

    const copyText = async () => {
        const body = buildMessage(shareTo.name);
        try {
            await navigator.clipboard.writeText(body);
            toast.success("Copied — paste it anywhere.");
            logReferral("copy", taskCount);
        } catch {
            toast.error("Copy failed. Long-press to select and copy.");
        }
    };

    const nativeShare = async () => {
        const body = buildMessage(shareTo.name);
        if (navigator.share) {
            try {
                await navigator.share({ title: "How you can help", text: body });
                logReferral("share", taskCount);
            } catch { /* user cancelled */ }
        } else {
            copyText();
        }
    };

    return (
        <div data-clarity-mask="true" className="space-y-10 animate-fade-in-up">
            <PageHero
                className="no-print"
                kicker="Family"
                kickerIcon={Users}
                title="Share with family"
                subtitle="Asking for help is hard. Build the list of specific ways family can pitch in, then send it in one tap."
                actions={<>
                    <button onClick={() => setShareOpen(true)} data-testid="family-share-open"
                        className="ml-btn-primary !min-h-[44px] !px-4">
                        <Share2 size={18} strokeWidth={1.6} /> Share with family
                    </button>
                    <button onClick={() => window.print()} data-testid="family-print" className="ml-btn-secondary !min-h-[44px] !px-4">
                        <Printer size={18} strokeWidth={1.6} /> Print
                    </button>
                </>}
            />

            <div className="hidden print:block">
                <h1 className="text-3xl font-heading font-semibold">How you can help</h1>
                <p className="text-sm text-[#4C5551]">
                    A list from {profile.caregiver_name || "your caregiver"} — anything checked is a real way to make a difference.
                </p>
                <hr className="my-3" />
            </div>

            {/* Stage-aware suggested tasks (Phase D) */}
            {suggestions && suggestions.tasks?.length > 0 && (
                <section className="ml-card p-6 md:p-7 bg-teal-soft/40 !border-teal-soft no-print"
                    data-testid="family-suggestions">
                    <div className="flex items-center gap-2.5 mb-3">
                        <Sparkles strokeWidth={1.6} className="text-teal" />
                        <h2 className="text-xl md:text-2xl font-heading font-medium text-teal-dark">
                            Suggested for your stage (FAST {suggestions.stage})
                        </h2>
                    </div>
                    <p className="text-sm text-[#1F2624]/80 mb-4">
                        Tap to add. These are the highest-value ways family can help right now.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {suggestions.tasks.map((t, i) => {
                            const alreadyOn = tasks.some(x => x.text === t.text);
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => addSuggestion(t.text)}
                                    disabled={alreadyOn}
                                    data-testid={`family-suggestion-${i}`}
                                    className={`text-left px-3.5 py-2 rounded-2xl text-sm font-medium border transition-all ${alreadyOn
                                        ? "bg-cream-dark text-[#6E7A74] border-[#E2E8F0] cursor-default"
                                        : "bg-white text-[#1F2624] border-teal-soft hover:bg-teal-soft hover:border-teal"
                                        }`}
                                >
                                    {alreadyOn ? "✓ " : "+ "}{t.text}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Share My Situation — manual input + AI draft */}
            <section className="ml-card p-6 md:p-8 no-print" data-testid="family-share-situation">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                        <Wand2 strokeWidth={1.6} className="text-teal" />
                        <h2 className="text-xl md:text-2xl font-heading font-medium">Share my situation</h2>
                    </div>
                    {suggestions && suggestions.caregiver_hours_per_week > 0 && (
                        <div className="inline-flex items-center gap-1.5 text-xs text-[#4C5551] bg-cream-dark px-3 py-1.5 rounded-2xl">
                            <Clock size={12} strokeWidth={1.8} />
                            You log {suggestions.caregiver_hours_per_week} hrs/week · burnout {suggestions.burnout_score}/10
                        </div>
                    )}
                </div>
                <p className="mt-2 text-[#4C5551]">
                    Write a short note family and friends can read so you don&apos;t have to keep retelling the story.
                    Type your own, or tap <span className="font-medium text-teal-dark">Draft this for me</span> and Lane will
                    write a first version you can edit.
                </p>

                <textarea
                    data-testid="family-situation-text"
                    value={situationText}
                    onChange={(e) => setSituationText(e.target.value)}
                    className="ml-input mt-5 min-h-[220px] py-3 leading-relaxed whitespace-pre-wrap"
                    placeholder={`Where you are right now in your own words.

For example:
"We're caring for Mom (early-middle stage Alzheimer's) at home. She still cooks but forgets the stove. I'm doing about 30 hrs/week and starting to fray. Here's what would actually help…"

You can also tap "Draft this for me" and Lane will write a starting version.`}
                />

                <div className="mt-4 flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={draftSituation}
                        disabled={situationLoading}
                        data-testid="family-situation-draft"
                        className="ml-btn-primary !min-h-[44px] !px-4 disabled:opacity-60"
                    >
                        <Sparkles size={16} strokeWidth={1.6} />
                        {situationLoading ? "Drafting…" : (hasDrafted ? "Draft another version" : "Draft this for me")}
                    </button>
                    <button
                        type="button"
                        onClick={copySituation}
                        data-testid="family-situation-copy"
                        disabled={!situationText.trim()}
                        className="ml-btn-secondary !min-h-[44px] !px-4 disabled:opacity-50"
                    >
                        <Copy size={16} strokeWidth={1.6} /> Copy text
                    </button>
                    <button
                        type="button"
                        onClick={emailSituation}
                        data-testid="family-situation-email"
                        disabled={!situationText.trim()}
                        className="ml-btn-secondary !min-h-[44px] !px-4 disabled:opacity-50"
                    >
                        <Mail size={16} strokeWidth={1.6} /> Email it
                    </button>
                    {situationText.trim() && (
                        <button
                            type="button"
                            onClick={clearSituation}
                            data-testid="family-situation-clear"
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#4C5551] hover:bg-cream-dark rounded-2xl"
                        >
                            <Trash2 size={14} strokeWidth={1.6} /> Clear
                        </button>
                    )}
                </div>

                <p className="mt-3 text-xs text-[#6E7A74]">
                    {hasDrafted
                        ? "Lane drafted this from your intake — edit freely to sound like you. Saved automatically."
                        : situationText
                            ? "Saved automatically — pick this up later from any device while signed in."
                            : "Tip: if you write a few sentences first, then tap Draft this for me, Lane will keep your words and add structure."}
                </p>
            </section>

            {!suggestions && (
                <section className="ml-card p-5 bg-yellow-soft/60 !border-yellow-soft no-print"
                    data-testid="family-no-intake-cta">
                    <div className="flex items-start gap-3">
                        <Sparkles size={18} strokeWidth={1.6} className="text-yellow-dark shrink-0 mt-0.5" />
                        <p className="text-sm text-[#1F2624]">
                            Want stage-specific suggested tasks and an auto-written situation summary?{" "}
                            <Link to="/intake" data-testid="family-take-intake"
                                className="text-yellow-dark font-medium underline underline-offset-2">Take the 3-min intake</Link>.
                        </p>
                    </div>
                </section>
            )}

            <section className="ml-card p-6 md:p-8">
                <div className="flex gap-2 mb-5 no-print">
                    <input
                        data-testid="family-new-task"
                        value={newTask}
                        onChange={e => setNewTask(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && add()}
                        className="ml-input"
                        placeholder="Add a way family could help…"
                    />
                    <button onClick={add} data-testid="family-add" className="ml-btn-primary">
                        <Plus size={18} strokeWidth={1.6} /> Add
                    </button>
                </div>

                <ul className="space-y-2" data-testid="family-tasks">
                    {tasks.map(t => (
                        <li key={t.id} className="flex items-center gap-3 bg-cream-dark/60 rounded-xl px-4 py-3" data-testid={`family-task-${t.id}`}>
                            <input
                                type="checkbox"
                                checked={t.done}
                                onChange={() => toggle(t.id)}
                                data-testid={`family-check-${t.id}`}
                                className="w-5 h-5 accent-teal cursor-pointer"
                            />
                            <span className={`flex-1 ${t.done ? "line-through text-[#4C5551]" : "text-[#1F2624]"}`}>{t.text}</span>
                            <button onClick={() => remove(t.id)} className="text-coral-dark no-print" data-testid={`family-remove-${t.id}`}>
                                <Trash2 size={16} strokeWidth={1.6} />
                            </button>
                        </li>
                    ))}
                </ul>
            </section>

            {/* Refer a family member — removed per design simplification.
                "Share with family" (the primary CTA) still uses the recipient name field
                inside the share dialog, so shareTo state remains for that purpose. */}

            {/* Share dialog */}
            {shareOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 no-print"
                    onClick={() => setShareOpen(false)}
                    data-testid="family-share-dialog"
                >
                    <div className="ml-card w-full max-w-lg p-6 md:p-8 bg-white" onClick={e => e.stopPropagation()}>
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <h2 className="text-2xl font-heading font-medium text-teal">Send to family</h2>
                            <button onClick={() => setShareOpen(false)} data-testid="family-share-close" className="text-[#4C5551] hover:text-[#1F2624]">
                                <X size={22} strokeWidth={1.6} />
                            </button>
                        </div>
                        <p className="text-sm text-[#4C5551] mb-5">
                            Pre-filled with {taskCount} task{taskCount === 1 ? "" : "s"} from your list. Pick how you want to send it.
                        </p>

                        <div>
                            <label className="ml-label" htmlFor="share-name">Their first name (optional)</label>
                            <input id="share-name" data-testid="family-share-name"
                                value={shareTo.name}
                                onChange={e => setShareTo({ ...shareTo, name: e.target.value })}
                                className="ml-input mb-4"
                                placeholder="e.g. Jamie"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <button onClick={sendEmail} data-testid="family-send-email" className="ml-btn-primary !justify-center">
                                <Mail size={18} strokeWidth={1.6} /> Email
                            </button>
                            <button onClick={sendSms} data-testid="family-send-sms" className="ml-btn-secondary !justify-center">
                                <MessageSquare size={18} strokeWidth={1.6} /> Text
                            </button>
                            <button onClick={copyText} data-testid="family-send-copy" className="ml-btn-coral !justify-center">
                                <Copy size={18} strokeWidth={1.6} /> Copy
                            </button>
                            <button onClick={nativeShare} data-testid="family-send-share" className="ml-btn-secondary !justify-center">
                                <Share2 size={18} strokeWidth={1.6} /> Share
                            </button>
                        </div>

                        <details className="mt-5 text-sm text-[#4C5551]">
                            <summary className="cursor-pointer font-medium">Preview message</summary>
                            <pre className="mt-2 whitespace-pre-wrap bg-cream-dark rounded-xl p-4 text-[#1F2624] text-sm leading-relaxed" data-testid="family-share-preview">
                                {buildMessage(shareTo.name)}
                            </pre>
                        </details>
                    </div>
                </div>
            )}

            <section className="ml-card p-6 md:p-8 bg-cream-dark !border-cream-dark no-print">
                <h2 className="text-xl font-heading font-medium text-teal mb-2">A note to share</h2>
                <p className="text-[#1F2624] leading-relaxed">
                    &ldquo;I love them, and I&apos;m tired. I&apos;m not asking for everything — I&apos;m asking for one thing.
                    If you can do something on this list, please pick one and let me know which. That alone
                    helps more than you know.&rdquo;
                </p>
            </section>
        </div>
    );
}
